import { DateTime, Interval } from 'luxon';
import React from 'react';
import { CalendarContent, CalendarDay, CalendarDayCondition, CalendarDayPlaceHolder, WeekDay } from './Calendar.styles';
import { ICalendarValue } from './Calendar';
import { ErrorMessage } from '../ErrorMessage';
import { mergeTests } from '../../test-utilities/mergeTests';
import { IHasTestData } from '../../test-utilities/IHasTestData';

const NUMBER_OF_DAYS_IN_6_ROWS = 42;
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export type CalendarDaysSelectionMode = 'list' | 'range';

const computeMonthData = (year: number, month: number) => {
  const dateTime = DateTime.local(year, month);
  const firstDayInMonth = dateTime.set({ day: 1 }).weekday !== 7 ? dateTime.set({ day: 1 }).weekday : 0;
  const lastDayInMonth = firstDayInMonth + dateTime.daysInMonth;
  const numbersOfDaysAfter = (NUMBER_OF_DAYS_IN_6_ROWS - lastDayInMonth) % 7;
  const previousMonthDisplayDate = DateTime.local(year, month).minus({ days: firstDayInMonth });
  const nextMonthDisplayDate = DateTime.local(year, month).plus({ months: 1 });
  return {
    firstDayInMonth,
    nextMonthDisplayDate,
    numbersOfDaysAfter,
    previousMonthDisplayDate,
    daysInMonth: dateTime.daysInMonth,
    previousMonthStart: previousMonthDisplayDate.day,
  };
};

const computeIsDisabled = (
  year: number,
  month: number,
  day: number,
  minDate?: DateTime,
  maxDate?: DateTime
): boolean => {
  let disable = false;
  switch (true) {
    case minDate && !maxDate:
  }
  if (minDate && !maxDate) {
    return DateTime.local(year, month, day) < minDate;
  }
  if (maxDate && !minDate) {
    return DateTime.local(year, month, day) > maxDate;
  }
  if (maxDate && minDate) {
    return DateTime.local(year, month, day) > maxDate || DateTime.local(year, month, day) < minDate;
  }
  return disable;
};

const computeIsDate = (year: number, month: number, day: number, date?: DateTime): boolean => {
  if (date === undefined || !date.isValid) {
    return false;
  }
  return date.year === year && date.month === month && date.day === day;
};

const computeDayCondition = (
  year: number,
  month: number,
  day: number,
  selectedDates: DateTime[],
  selectionMode: CalendarDaysSelectionMode,
  minDate?: DateTime,
  maxDate?: DateTime,
  highlightedDate?: DateTime
): CalendarDayCondition => {
  switch (selectionMode) {
    case 'list':
      return selectedDates.some((value) => value.year === year && value.month === month && value.day === day)
        ? CalendarDayCondition.selected
        : computeIsDisabled(year, month, day, minDate, maxDate)
          ? computeIsDate(year, month, day, highlightedDate)
            ? CalendarDayCondition.disabledHighlighted
            : CalendarDayCondition.disable
          : computeIsDate(year, month, day, highlightedDate)
            ? CalendarDayCondition.highlighted
            : CalendarDayCondition.default;
    case 'range': {
      const isWithinInclusive =
        selectedDates.length === 2 &&
        Interval.fromDateTimes(selectedDates[0], selectedDates[1]).contains(DateTime.local(year, month, day));

      const isSelectedFirst =
        selectedDates[0]?.year === year && selectedDates[0]?.month === month && selectedDates[0]?.day === day;
      const isSelectedLast =
        selectedDates[1]?.year === year && selectedDates[1]?.month === month && selectedDates[1]?.day === day;

      return isSelectedFirst
        ? CalendarDayCondition.selectedLeft
        : isSelectedLast
          ? CalendarDayCondition.selectedRight
          : isWithinInclusive
            ? CalendarDayCondition.within
            : computeIsDisabled(year, month, day, minDate, maxDate)
              ? CalendarDayCondition.disable
              : CalendarDayCondition.default;
    }
  }
};

export interface ICalendarDaysProps extends IHasTestData {
  displayDate: { month: number; year: number };
  minDate?: DateTime;
  maxDate?: DateTime;
  onDayClick: (value: ICalendarValue) => void;
  selectedDates: DateTime[];
  selectionMode: CalendarDaysSelectionMode;
  showPreviousMonthDays?: boolean;
  showNextMonthDays?: boolean;
  highlightedDate?: DateTime | 'today';
}

/**
 * This component can't render Date ranges that include **more than** an assumed "start" & "end"
 * This will render an error message an invalid date range is provided
 * */
export const CalendarDays: React.FC<ICalendarDaysProps> = ({
  displayDate,
  minDate,
  maxDate,
  onDayClick,
  selectedDates,
  selectionMode,
  showNextMonthDays = true,
  showPreviousMonthDays = true,
  highlightedDate,
  'data-test-id': dataTestId,
  'data-test-class': dataTestClass,
}) => {
  const {
    daysInMonth,
    firstDayInMonth,
    nextMonthDisplayDate,
    numbersOfDaysAfter,
    previousMonthDisplayDate,
    previousMonthStart,
  } = computeMonthData(displayDate.year, displayDate.month);
  const isDateRangeSelectionAllowed = selectionMode === 'range' && selectedDates.length <= 2;
  const isDateListSelectionAllowed = selectionMode === 'list';
  let highlightedDateVal: DateTime | undefined;
  if (highlightedDate === 'today') {
    highlightedDateVal = DateTime.local();
  } else {
    highlightedDateVal = highlightedDate;
  }
  return isDateRangeSelectionAllowed || isDateListSelectionAllowed ? (
    <CalendarContent>
      {WEEKDAYS.map((day) => (
        <WeekDay key={day}>{day}</WeekDay>
      ))}
      {Array(firstDayInMonth)
        .fill(undefined)
        .map((_, index) => (
          <CalendarDayPlaceHolder
            key={`before ${index}`}
            data-test-class={mergeTests(dataTestClass, 'calendar-day-placeholder')}
            condition={
              showPreviousMonthDays
                ? computeDayCondition(
                    previousMonthDisplayDate.year,
                    previousMonthDisplayDate.month,
                    previousMonthStart + index,
                    selectedDates,
                    selectionMode,
                    minDate,
                    maxDate,
                    highlightedDateVal
                  )
                : CalendarDayCondition.default
            }
          >
            {showPreviousMonthDays ? previousMonthStart + index : ' '}
          </CalendarDayPlaceHolder>
        ))}
      {Array(daysInMonth)
        .fill(undefined)
        .map((_, index) => {
          const day = index + 1;
          return (
            <CalendarDay
              condition={computeDayCondition(
                displayDate.year,
                displayDate.month,
                day,
                selectedDates,
                selectionMode,
                minDate,
                maxDate,
                highlightedDateVal
              )}
              key={day}
              data-test-class={mergeTests(dataTestClass, 'calendar-day')}
              onClick={
                !computeIsDisabled(displayDate.year, displayDate.month, day, minDate, maxDate)
                  ? (e: React.MouseEvent<HTMLDivElement>) => {
                      const newDate = DateTime.local(displayDate.year, displayDate.month, day).toISODate();
                      onDayClick({
                        error: null,
                        event: e,
                        value: newDate,
                      });
                    }
                  : undefined
              }
            >
              <div>{day}</div>
            </CalendarDay>
          );
        })}
      {Array(numbersOfDaysAfter)
        .fill(undefined)
        .map((_, index) => (
          <CalendarDayPlaceHolder
            key={`after ${index}`}
            data-test-class={mergeTests(dataTestClass, 'calendar-day-placeholder')}
            condition={
              showNextMonthDays
                ? computeDayCondition(
                    nextMonthDisplayDate.year,
                    nextMonthDisplayDate.month,
                    index + 1,
                    selectedDates,
                    selectionMode,
                    minDate,
                    maxDate,
                    highlightedDateVal
                  )
                : CalendarDayCondition.default
            }
          >
            {showNextMonthDays ? index + 1 : ' '}
          </CalendarDayPlaceHolder>
        ))}
    </CalendarContent>
  ) : isDateRangeSelectionAllowed ? (
    <ErrorMessage>The selected date range is not valid</ErrorMessage>
  ) : null;
};
